import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Company } from "@somasi/shared";
import { CountryField } from "@/components/country-field";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { api, type CompanyDraft } from "@/integrations/aws/resources";
import { liteList } from "@/lib/query-keys";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/utils";
import { db } from "@/integrations/aws/data";
import {
  PRICING_MODES,
  PRICING_MODE_HINT,
  PRICING_MODE_LABEL,
  asPricingMode,
  usesCompanySchedule,
  type PricingMode,
} from "@/lib/pricing";
import { ClientGroupSelect, invalidateCompanyLists } from "@/components/entity-select";
import { Truncated } from "@/components/truncated";
import { ImportButton } from "@/components/import-dialog";
import {
  CLIENT_GROUP_IDENTITY_COLUMNS,
  buildClientGroupIndex,
  companyTemplateColumns,
  validateCompanyRows,
  type ClientGroupIdentity,
  type CompanyImportContext,
  type ExistingCompany,
} from "@/lib/company-import";
import { DEFAULT_JURISDICTION, STATUS_LABEL, STATUS_TONE } from "@/lib/companies";
import { AfrPeriodSummary } from "@/components/afr-period-summary";
import { FyEndField } from "@/components/fy-end-field";
import { ExportMenu } from "./client-groups";
import { z } from "zod";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

const GROUP_OPTIONS = liteList("client_groups_lite", "id, name");

export const Route = createFileRoute("/_authenticated/companies")({
  head: () => ({
    meta: [
      { title: "Companies — Somasi Ops" },
      {
        name: "description",
        content: "Master list of BVI and other companies under Somasi's care.",
      },
    ],
  }),
  component: CompaniesPage,
});

/**
 * Re-exported for the pages that read a company's status label from here — the
 * client group page among them. The maps themselves are in `@/lib/companies`,
 * so the exports can reach them without importing a route module.
 */
export { STATUS_LABEL, STATUS_TONE };

/**
 * Everything the import needs of what is already stored, read fresh.
 *
 * Deliberately not a `useQuery`. It is called when the template is downloaded
 * and again when a file is parsed, and both have to see the register as it
 * stands at that moment: a template built from a cached list is a Client Group
 * dropdown missing the group created this morning, and the operator only finds
 * out at the preview — by which time the file is filled in. The point of
 * generating the file on every click is lost if what is behind it is held
 * anywhere. Same call `work-log.tsx` makes for its company dropdown.
 *
 * Three reads in parallel: the groups the dropdown offers, the countries the
 * Jurisdiction column offers, and the names and registration numbers on file,
 * which is what makes "this company already exists" a warning the operator sees
 * before committing rather than a duplicate they find afterwards.
 */
async function fetchCompanyImportContext(): Promise<CompanyImportContext> {
  const [groups, countries, companies] = await Promise.all([
    api.clientGroups.list().all().select(CLIENT_GROUP_IDENTITY_COLUMNS).order("name"),
    api.countries.list().all().eq("is_active", true).order("name"),
    api.companies.list().all().select("legal_name, registration_number"),
  ]);
  for (const { error } of [groups, countries, companies]) {
    if (error) throw new Error(error.message);
  }
  return {
    groups: buildClientGroupIndex((groups.data ?? []) as ClientGroupIdentity[]),
    jurisdictions: ((countries.data ?? []) as { name: string }[])
      .map((country) => country.name)
      .filter(Boolean),
    existing: (companies.data ?? []) as ExistingCompany[],
  };
}

function CompaniesPage() {
  // `canAdmin` gates the import: `POST /import/commit` allows the companies
  // table to admins only, so a button anyone else could press would be one that
  // fails at the commit, after the file has been filled in and previewed.
  const navigate = useNavigate();
  const { canEdit, canAdmin } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");

  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  // The status and client group filters and the text search are all applied by
  // the API, so changing any of them refetches. They are part of the query key,
  // which is what makes that happen — and what keeps each combination cached
  // separately.
  //
  // With the filters on "all" and the box empty nothing is sent but the sort, so
  // the default view is a plain `GET /companies?order=updated_at.desc`: whatever
  // changed most recently, first. That stays the initial sort even though no
  // column shows `updated_at` — the register opens on what was just touched, and
  // a header click switches to a visible column from there.
  const sort = useListSort({ column: "updated_at", ascending: false });
  const filters = { status: statusFilter, group: groupFilter, query, sort: sort.sort };
  const pager = usePagination({ resetKey: filters });

  /** The list the filters describe — the table pages it, the export takes it whole. */
  const filteredList = () => {
    let list = api.companies.list();
    if (statusFilter !== "all") list = list.eq("status", statusFilter);
    if (groupFilter !== "all") list = list.eq("client_group_id", groupFilter);
    // One `or=` group, so the text has to appear in one of the three columns
    // rather than in all of them — which is what a search box means.
    if (query) {
      list = list.or([
        { column: "legal_name", op: "ilike", value: `%${query}%` },
        { column: "trading_name", op: "ilike", value: `%${query}%` },
        { column: "registration_number", op: "ilike", value: `%${query}%` },
      ]);
    }
    list = list.order(sort.sort.column, { ascending: sort.sort.ascending });
    // Ties — every company sharing a jurisdiction or a status — fall back to the
    // name, so a page break never lands somewhere different between two requests.
    if (sort.sort.column !== "legal_name") list = list.order("legal_name");
    return list;
  };

  const {
    data: result,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["companies", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as Company[], total: count ?? 0 };
    },
    // Keep the previous page on screen while the new filter loads, so the table
    // does not blank out on every change.
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;

  // The group filter's options are `ClientGroupSelect`'s business now: it opens
  // on the first page of groups and sends what is typed to the API, so the list
  // does not have to be downloaded to be filtered and does not quietly stop at
  // whatever the first read returned.

  // Every filter is the API's now, so the rows on screen are already the
  // answer — there is nothing left to narrow here.
  const filtered = data;

  const sel = useRowSelection(filtered);

  // Seven data columns, plus the checkbox and actions columns editors get.
  const columnCount = canEdit ? 9 : 7;
  const filtersSet = Boolean(query) || statusFilter !== "all" || groupFilter !== "all";

  /*
   * Editing is a page now, not a dialog — see `companies_.$companyId.tsx`. The
   * AFR history it carries is a list of financial years with a filing action on
   * each and documents behind those, which is two modals deep inside a dialog.
   */
  const openNew = () =>
    void navigate({ to: "/companies/$companyId", params: { companyId: "new" } });
  const openEdit = (row: Company) =>
    void navigate({ to: "/companies/$companyId", params: { companyId: row.id } });
  /**
   * The table, and the pickers and name lookups elsewhere.
   *
   * `["companies"]` does not reach them: React Query matches key elements, so
   * `"companies_picker"` is a different key rather than a child of this one.
   */
  const refreshCompanies = (client: typeof qc) => {
    client.invalidateQueries({ queryKey: ["companies"] });
    invalidateCompanyLists(client);
  };

  const remove = async (row: Company) => {
    if (!(await confirmDelete(`“${row.legal_name}”`))) return;
    const { error } = await api.companies.remove(row.id);
    if (error) return notify.error(error);
    notify.success("Company deleted");
    refreshCompanies(qc);
  };

  const exportRows = async () => {
    const { data: all, error } = await filteredList().all();
    if (error) throw new Error(error.message);
    return ((all ?? []) as Company[]).map((c) => ({
      "Legal Name": c.legal_name,
      "Trading Name": c.trading_name ?? "",
      "Client Group": c.client_group_name ?? "—",
      "Registration Number": c.registration_number ?? "",
      Jurisdiction: c.jurisdiction ?? "",
      Status: STATUS_LABEL[c.status],
      "Incorporation Date": c.incorporation_date ?? "",
      "AFR Due": c.afr_due_date ?? "",
      "QB Customer": c.quickbooks_customer_name ?? "",
      "Nature of Business": c.nature_of_business ?? "",
      "Business Description": c.business_description ?? "",
    }));
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Companies"
          description="Every legal entity under Somasi's care — searchable, filterable, exportable."
          actions={
            <>
              <ExportMenu
                filenameBase="somasi-companies"
                rows={exportRows}
                pdfCols={[
                  { header: "Legal Name", dataKey: "Legal Name" },
                  { header: "Client Group", dataKey: "Client Group" },
                  // Keyed as `exportRows` writes it — "Registration #" matched no
                  // column, so the PDF printed this one blank for every company.
                  { header: "Reg #", dataKey: "Registration Number" },
                  { header: "Jurisdiction", dataKey: "Jurisdiction" },
                  { header: "Status", dataKey: "Status" },
                  { header: "AFR Due", dataKey: "AFR Due" },
                ]}
                title="Companies Master List"
              />
              {canAdmin && (
                <ImportButton
                  table="companies"
                  entityLabel="company"
                  entityLabelPlural="companies"
                  template={{
                    filename: "somasi-companies-template.xlsx",
                    sheetName: "Companies",
                    // A function, so the file is built from the register as it
                    // stands on the click: the Client Group dropdown has to
                    // offer the group created this morning, and a template held
                    // in a variable would not.
                    columns: async () => {
                      const { groups, jurisdictions } = await fetchCompanyImportContext();
                      return companyTemplateColumns(groups, jurisdictions);
                    },
                  }}
                  hint="Legal Name is all a row needs. Client Group, Jurisdiction, Status and the service flags are dropdowns of what actually exists — pick, do not type."
                  validate={async (parsed) =>
                    validateCompanyRows(parsed, await fetchCompanyImportContext())
                  }
                  onImported={() => refreshCompanies(qc)}
                />
              )}
              {canEdit && (
                <Button onClick={openNew}>
                  <Plus className="mr-2 h-4 w-4" /> New company
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search legal name, trading name, registration number…"
          />
          <FilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-40"
            options={[
              { value: "all", label: "All statuses" },
              ...(Object.keys(STATUS_LABEL) as Company["status"][]).map((s) => ({
                value: s,
                label: STATUS_LABEL[s],
              })),
            ]}
          />
          <ClientGroupSelect
            value={groupFilter === "all" ? "" : groupFilter}
            onChange={(id) => setGroupFilter(id || "all")}
            placeholder="All client groups"
            clearable
            clearLabel="All client groups"
            className="w-56"
            contentClassName="w-72 p-0"
          />
          {isFetching && <div className="text-xs text-muted-foreground">Loading…</div>}
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.companies}
            entityLabel="company"
            onDone={() => refreshCompanies(qc)}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="legal_name">
                Legal name
              </SortHeader>
              {/* The group's name is resolved from another table, so there is no
                  column on the company to order by. */}
              <TableHead>Client group</TableHead>
              <SortHeader sort={sort} column="registration_number">
                Registration Number
              </SortHeader>
              <SortHeader sort={sort} column="jurisdiction">
                Jurisdiction
              </SortHeader>
              <SortHeader sort={sort} column="incorporation_date">
                Incorporated
              </SortHeader>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <SortHeader sort={sort} column="afr_due_date">
                AFR due
              </SortHeader>
              {/* Edit and delete are the only actions, so a read-only operator
                  would get an empty column. */}
              {canEdit && (
                <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : filtered.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                {filtersSet ? "No companies match these filters." : "No companies yet."}
              </TableMessageRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.id} data-state={sel.isSelected(c.id) ? "selected" : undefined}>
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={c.id} />
                    </TableCell>
                  )}
                  <TableCell className="max-w-xs">
                    {/* The name is the way in, now that editing is a page. */}
                    <Link
                      to="/companies/$companyId"
                      params={{ companyId: c.id }}
                      className="hover:underline"
                    >
                      <Truncated text={c.legal_name} className="font-medium" />
                    </Link>
                    {c.trading_name && (
                      <Truncated
                        text={`t/a ${c.trading_name}`}
                        className="text-xs text-muted-foreground"
                      />
                    )}
                  </TableCell>
                  <TableCell>{c.client_group_name ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.registration_number ?? "—"}
                  </TableCell>
                  <TableCell>{c.jurisdiction ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(c.incorporation_date)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_TONE[c.status]} hover:opacity-90`}>
                      {STATUS_LABEL[c.status]}
                    </Badge>
                  </TableCell>
                  {/* The due date links into this company's own financial years,
                      so the register is reachable from the company rather than
                      being a separate screen to go and search in. A company with
                      no history yet has no link — there is nothing to show. */}
                  <TableCell className="whitespace-nowrap">
                    {c.afr_fy_label ? (
                      <Link
                        to="/afr"
                        search={{ company: c.id }}
                        className="hover:underline"
                        title={`${c.afr_fy_label} — open in AFR register`}
                      >
                        {formatDate(c.afr_due_date)}
                      </Link>
                    ) : (
                      formatDate(c.afr_due_date)
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <RowActions>
                        <RowAction label="Edit" onClick={() => openEdit(c)}>
                          <Pencil />
                        </RowAction>
                        <RowAction label="Delete" destructive onClick={() => remove(c)}>
                          <Trash2 />
                        </RowAction>
                      </RowActions>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          pagination={pager}
          total={total}
          loading={isLoading}
          unit="company"
          units="companies"
        />
      </ListPageTable>
    </ListPage>
  );
}
